import PropTypes from 'prop-types'
import classNames from 'classnames'

import PAGE_QUERY from 'graphql/page.query'

import Layout from 'components/Layout'
import WordpressSEO from 'components/WordpressSEO'
import WordpressLink from 'components/WordpressLink'
import Blocks from 'components/Blocks'

import s from './[...slug].module.css'
import StateInitializer from '../../components/StateInitializer'
import getGlobalSettings from '../../helpers/getGlobalSettings'
import getSites from '../../helpers/getSites'
import slugify from '../../helpers/slugify'
import PAGES_QUERY from '../../graphql/pages.query'
import getSitesForStaticPaths from '../../helpers/getSitesForStaticPaths'
import { createApolloClient } from '../../lib/createApolloClient'
import * as Sentry from '@sentry/nextjs'

function Page({
  globalSettings,
  sites,
  siteSlug,
  site = '',
  slug = '',
  title = '',
  seo = {},
  acfBlocks = {},
  acfPageSettings = {},
}) {
  const menu = acfPageSettings?.menu?.nodes?.[0]?.acfMenuItems?.menu[0]?.items

  const blocks = acfBlocks ? <Blocks postType="Page">{acfBlocks}</Blocks> : null

  return (
    <StateInitializer
      globalSettings={globalSettings}
      sites={sites}
      siteSlug={siteSlug}
    >
      <Layout globalSettings={globalSettings}>
        <WordpressSEO url={`/${site}/${slug[0]}`} title={title} seo={seo} />
        <div
          className={classNames(s.container, {
            [s.containerWithMenu]: menu,
          })}
        >
          {menu ? (
            <>
              <nav className={s.menu}>
                {menu.map((l) => (
                  <WordpressLink key={l.label} className={s.menuLink} {...l} />
                ))}
              </nav>
              <div className={s.content}>{blocks}</div>
            </>
          ) : (
            blocks
          )}
        </div>
      </Layout>
    </StateInitializer>
  )
}

Page.propTypes = {
  globalSettings: PropTypes.object.isRequired,
  sites: PropTypes.array.isRequired,
  siteSlug: PropTypes.string.isRequired,
}

export async function getStaticProps({ params }) {
  const { site, slug } = params

  const apolloClient = createApolloClient()

  const [globalSettings, sites, { data, error }] = await Promise.all([
    getGlobalSettings(site),
    getSites(),
    apolloClient.query({
      query: PAGE_QUERY,
      context: { site },
      variables: { slug: slug[0] },
    }),
  ])

  if (error) {
    Sentry.addBreadcrumb({
      message: `Error loading Page props - ${error}`,
      data: { params, error },
      level: 'error',
    })
    Sentry.captureException(new Error(`Error loading Page props - ${error}`))
    console.error(`Error loading Page props - ${error}`)
  }

  return {
    props: {
      ...params,
      ...data.pageBy,
      globalSettings,
      sites,
      siteSlug: site,
    },
    revalidate: 60 * 60 * 24, // 24 hours
  }
}

export async function getStaticPaths() {
  const paths = []

  await getSitesForStaticPaths(
    ({ site }) => ({
      query: PAGES_QUERY,
      context: { site },
    }),
    (data, { site }) =>
      data.pages.nodes
        .filter((n) => !['contact', 'about', 'news', 'login'].includes(n.slug))
        .map((n) => {
          paths.push({ params: { site, slug: [n.slug] } })

          ;(n.acfBlocks.contentBlocks || [])
            .filter(
              (b) =>
                b.__typename === 'AcfBlocksContentBlocksTextSectionsLayout' &&
                b.sections.length > 1
            )
            .forEach((b) =>
              b.sections.slice(1).forEach((section) => {
                const sectionSlug = slugify(section.subSections[0].menuLabel)
                paths.push({
                  params: {
                    site,
                    slug: [n.slug, sectionSlug],
                  },
                })
              })
            )
        })
  )

  return {
    paths,
    fallback: false,
  }
}

export default Page
